/**
 * @file task-texts.ts
 * @description Тексты строки и окна задачи (phase19.31, SC60; crm.md T1–T7):
 * срок по группе, «к кому относится», исполнитель, история назначений.
 *
 * Чистые функции с явным поясом — их проверяют тесты, а строка и окно
 * только раскладывают готовые подписи. Просрочка — не слово в тексте, а
 * признак: значок янтарного тона ставит разметка.
 */

import type { ActivityDto, TaskHistoryEntry } from "../../../lib/api-crm";
import type { TaskGroupKey } from "./task-groups";

/** «19.09, 18:11» по поясу. */
export function dayTimeInZone(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleString("ru-RU", {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** «18:11» по поясу. */
function timeInZone(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleTimeString("ru-RU", { timeZone, hour: "2-digit", minute: "2-digit" });
}

/**
 * Срок в строке: в «Сегодня» и «Завтра» день уже в заголовке группы —
 * только время; в «Просрочено» и «Позже» — день и время; у выполненной —
 * когда выполнена; без срока — пусто.
 */
export function taskDueText(
  task: Pick<ActivityDto, "dueAt" | "completedAt">,
  group: TaskGroupKey,
  timeZone: string,
): string {
  if (group === "done") return task.completedAt ? `выполнено ${dayTimeInZone(task.completedAt, timeZone)}` : "выполнено";
  if (!task.dueAt || Number.isNaN(Date.parse(task.dueAt))) return "";
  if (group === "today" || group === "tomorrow") return timeInZone(task.dueAt, timeZone);
  return dayTimeInZone(task.dueAt, timeZone);
}

/** Связь задачи кроме клиента: сделка или заказ. */
export type TaskLink = { kind: "deal"; id: string; text: string } | { kind: "order"; id: string; text: string };

type RelationInput = Pick<ActivityDto, "dealId" | "dealTitle" | "orderId" | "orderNumber">;

/** Сделка «…» или заказ Z-…; заказ важнее — запись из ленты заказа. */
export function taskLink(task: RelationInput): TaskLink | null {
  if (task.orderId && task.orderNumber) return { kind: "order", id: task.orderId, text: `заказ ${task.orderNumber}` };
  if (task.dealId && task.dealTitle) return { kind: "deal", id: task.dealId, text: `сделка «${task.dealTitle}»` };
  return null;
}

/** «Мария Ч. · сделка «Подарочный набор»» — к кому относится задача. */
export function taskRelationText(task: RelationInput & Pick<ActivityDto, "customerName">): string {
  return [task.customerName, taskLink(task)?.text].filter(Boolean).join(" · ");
}

/** Исполнитель или «Без исполнителя». */
export function taskAssigneeText(task: Pick<ActivityDto, "assigneeName">): string {
  return task.assigneeName ?? "Без исполнителя";
}

/** История задачи: поле ответа, а у старых ответов — `payload.history`. */
export function taskHistory(task: Pick<ActivityDto, "history" | "payload">): TaskHistoryEntry[] {
  if (task.history) return task.history;
  const payload = task.payload;
  if (typeof payload !== "object" || payload === null || Array.isArray(payload)) return [];
  const raw = (payload as { history?: unknown }).history;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (e): e is TaskHistoryEntry =>
      typeof e === "object" && e !== null && typeof (e as { type?: unknown }).type === "string" && typeof (e as { at?: unknown }).at === "string",
  );
}

/** Строка истории без даты: что произошло и кто это сделал. */
export function historyEntryText(entry: TaskHistoryEntry): string {
  switch (entry.type) {
    case "ASSIGNED": {
      const by = entry.by ? ` · ${entry.by.name}` : " · автоматически";
      if (!entry.to) return `Исполнитель снят${entry.from ? ` (${entry.from.name})` : ""}${by}`;
      if (!entry.from) return `Назначена: ${entry.to.name}${by}`;
      return `Исполнитель: ${entry.from.name} → ${entry.to.name}${by}`;
    }
    case "ASSIGNEE_RELEASED":
      return `Исполнитель ${entry.from.name} снят — сотрудник больше не работает с клиентами`;
    case "COMPLETED":
      return `Отмечена выполненной · ${entry.by.name}`;
    case "REOPENED":
      return `Возвращена в работу · ${entry.by.name}`;
  }
}
